import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import type {
  DataEntityDefinition,
  LedgerRecordEnvelopeDefinition,
  PlatformDataMapping,
  PublishedGroundworksDimensionDefinition,
  PublishedIntegrationClassificationAnchor,
  StandardMapping,
  StructuredValueSchema,
  StructuredValueSchemaDefinition,
  ValueSchemaBinding
} from "@apf/framework-config";
import { Glyph } from "@apf/design-system/glyphs";
import { useCopy, useFramework } from "../framework";
import { iconFor } from "../components/icons";
import { AbsenceBlock, EmptyState, FactBand, PageHeader, PlaceLine, StatusMark, humanize, placePart } from "../components/ui";
import { PlaceTrail } from "../components/PlaceTrail";
import { businessModulePath, groundworksEntityPath } from "../catalogue";

type DataMapping = PlatformDataMapping;
type FrameworkSnapshot = ReturnType<typeof useFramework>["snapshot"];
type CopyResolver = ReturnType<typeof useCopy>;

/** Used where threading a resolver is disproportionate; returns the declared English fallback. */
const literalCopy: CopyResolver = (key: string, fallback?: string) => fallback ?? key;

const catalogueContentTargetId = "data-model-catalogue-content";

interface ProjectedGroundworksDimension {
  id: string;
  label: string;
  definition: string;
  owner: string;
  members: string[];
  membersEmptyLabel: string;
  applicabilityTargets: string[];
  assignmentCarrierEntityIds: string[];
  overrideCarrierEntityIds: string[];
  resolutionRuleId: string;
  valueSource: string;
  relatedEntityIds: string[];
  searchText: string;
}

interface ProjectedClassificationAnchor {
  id: string;
  role: string;
  objectLabel: string;
  attributeLabel: string;
  canonicalTarget: string;
  authority: string;
  standardIds: string[];
  businessEffectIds: string[];
}

interface ProjectedClassificationAnchorGroup {
  systemId: string;
  systemLabel: string;
  anchors: ProjectedClassificationAnchor[];
}

const ArrowRight = iconFor("arrow-right");
const Search = iconFor("search");

function uniqueStrings(values: readonly string[]) {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

function sourceAuthorityLabel(field: DataEntityDefinition["fields"][number], copy: CopyResolver) {
  return field.sourceAuthority
    ? `${humanize(field.sourceAuthority.kind)} · ${field.sourceAuthority.id}`
    : copy("page.groundworks.contract.no-source-authority", "No separate source authority declared");
}

function standardMappingTarget(mapping: StandardMapping) {
  return mapping.documentId ?? mapping.conceptId ?? humanize(mapping.mappingKind);
}

function freshnessContractLabel(entity: DataEntityDefinition, copy: CopyResolver) {
  const contract = entity.freshnessContract;
  return `${humanize(contract.observationMode)} · ${humanize(contract.lastGoodPolicy)} · ${humanize(contract.degradedAction)}${contract.sourceHealthRequired ? ` · ${copy("page.groundworks.contract.source-health-required", "source health required")}` : ""}`;
}

function provenanceContractLabel(entity: DataEntityDefinition, copy: CopyResolver) {
  const contract = entity.provenanceContract;
  return uniqueStrings([
    contract.sourceAuthorityRequired ? copy("page.groundworks.contract.provenance.source-authority", "source authority") : "",
    contract.sourceVersionRequired ? copy("page.groundworks.contract.provenance.source-version", "source version") : "",
    contract.transformationVersionRequired ? copy("page.groundworks.contract.provenance.transformation-version", "transformation version") : "",
    contract.actorRequired ? copy("page.groundworks.contract.provenance.actor", "actor") : "",
    contract.evidenceRequired ? copy("page.groundworks.contract.provenance.evidence", "evidence") : "",
    contract.limitationsRequired ? copy("page.groundworks.contract.provenance.material-limitations", "material limitations") : ""
  ]).join(", ");
}

function accessContractLabel(entity: DataEntityDefinition, copy: CopyResolver) {
  const contract = entity.accessContract;
  return `${contract.actionClasses.map(humanize).join(", ")} · ${copy("page.groundworks.contract.scoped-by", "scoped by")} ${contract.scopeAttributes.map(humanize).join(", ")} · ${humanize(contract.deletePolicy)}`;
}

function resolveGroundworksDimensions(
  snapshot: FrameworkSnapshot,
  copy: ReturnType<typeof useCopy>
): ProjectedGroundworksDimension[] {
  const dimensionDefinitions = (snapshot.groundworks as FrameworkSnapshot["groundworks"] & {
    dimensions?: PublishedGroundworksDimensionDefinition[];
  }).dimensions ?? [];
  const entityIds = new Set(snapshot.groundworks.entities.map((entity) => entity.id));

  return dimensionDefinitions.map((dimension) => {
    const applicabilityTargets = uniqueStrings(dimension.applicabilityTargetIds);
    const assignmentCarrierEntityIds = uniqueStrings(
      dimension.assignmentCarrierEntityIds.filter((entityId) => entityIds.has(entityId))
    );
    const overrideCarrierEntityIds = uniqueStrings(
      (dimension.overrideCarrierEntityIds ?? []).filter((entityId) => entityIds.has(entityId))
    );
    const relatedEntityIds = uniqueStrings([
      ...applicabilityTargets.filter((targetId) => entityIds.has(targetId)),
      ...assignmentCarrierEntityIds,
      ...overrideCarrierEntityIds
    ]);
    const members = [...dimension.members]
      .sort((left, right) => left.order - right.order)
      .map((member) => copy(member.labelKey));

    return {
      id: dimension.id,
      label: copy(dimension.labelKey),
      definition: copy(dimension.definitionKey),
      owner: dimension.owner,
      members,
      membersEmptyLabel: dimension.definitionType === "tenant-defined"
        ? copy("page.groundworks.dimensions.members-empty.tenant", "Tenant taxonomy records")
        : copy("page.groundworks.dimensions.members-empty.framework", "No framework members"),
      applicabilityTargets,
      assignmentCarrierEntityIds,
      overrideCarrierEntityIds,
      resolutionRuleId: dimension.resolutionRuleId,
      valueSource: humanize(dimension.definitionType),
      relatedEntityIds,
      searchText: [
        dimension.id,
        copy(dimension.labelKey),
        copy(dimension.definitionKey),
        ...members,
        ...applicabilityTargets,
        ...assignmentCarrierEntityIds,
        ...overrideCarrierEntityIds,
        dimension.resolutionRuleId,
        dimension.definitionType,
        dimension.siteFacet?.parameter ?? "",
        dimension.siteFacet?.activation ?? ""
      ].join(" ").toLowerCase()
    };
  });
}

function resolveClassificationAnchors(snapshot: FrameworkSnapshot): ProjectedClassificationAnchorGroup[] {
  return snapshot.integrations.systemProfiles.flatMap((systemProfile) => {
    const anchors = (systemProfile.classificationAnchors ?? []).map((anchor: PublishedIntegrationClassificationAnchor) => ({
      id: anchor.id,
      role: humanize(anchor.objectRole),
      objectLabel: anchor.objectLabel,
      attributeLabel: anchor.attributeLabel,
      canonicalTarget: `${anchor.canonicalEntityId}.${anchor.canonicalFieldId}`,
      authority: humanize(anchor.authority),
      standardIds: anchor.standardIds ?? [],
      businessEffectIds: anchor.businessEffectIds.map(humanize)
    }));

    return anchors.length ? [{
      systemId: systemProfile.id,
      systemLabel: systemProfile.name,
      anchors
    }] : [];
  });
}

function dimensionMatchesEntity(
  entity: DataEntityDefinition,
  dimensionIds: readonly string[],
  dimensionsById: ReadonlyMap<string, ProjectedGroundworksDimension>
) {
  if (!dimensionIds.length) return true;
  return dimensionIds.some((dimensionId) => dimensionsById.get(dimensionId)?.relatedEntityIds.includes(entity.id));
}

function dimensionSearchTextForEntity(entity: DataEntityDefinition, dimensions: readonly ProjectedGroundworksDimension[]) {
  return dimensions
    .filter((dimension) => dimension.relatedEntityIds.includes(entity.id))
    .map((dimension) => dimension.searchText)
    .join(" ");
}

function mappingCount(entity: DataEntityDefinition) {
  return entity.fields.reduce((total, field) => total + field.standardMappings.length, 0);
}

function resolveValueSchema(
  binding: ValueSchemaBinding,
  definitions: readonly StructuredValueSchemaDefinition[],
  copy: CopyResolver = literalCopy
): { label: string; schema: StructuredValueSchema } | undefined {
  if (binding.mode === "inline") return { label: `${copy("page.groundworks.value-schemas.inline-schema", "Inline schema")} v${binding.schemaVersion}`, schema: binding.schema };
  const definition = definitions.find((candidate) => candidate.id === binding.schemaId && candidate.version === binding.schemaVersion);
  return definition ? { label: `${definition.label} · v${definition.version}`, schema: definition.schema } : undefined;
}

function schemaSearchText(
  schema: StructuredValueSchema,
  definitions: readonly StructuredValueSchemaDefinition[],
  depth = 0,
  visited = new Set<string>()
): string {
  if (depth > 4) return "";
  return schema.properties.flatMap((property) => {
    if (!property.valueSchema) return [property.id, property.type, property.format ?? "", property.listSemantics ?? ""];
    const key = property.valueSchema.mode === "registry" ? `${property.valueSchema.schemaId}@${property.valueSchema.schemaVersion}` : `inline-${depth}-${property.id}`;
    if (visited.has(key)) return [property.id, property.type, key];
    const nextVisited = new Set(visited).add(key);
    const resolved = resolveValueSchema(property.valueSchema, definitions);
    return [property.id, property.type, property.format ?? "", key, resolved ? schemaSearchText(resolved.schema, definitions, depth + 1, nextVisited) : ""];
  }).join(" ");
}

function SchemaTree({
  schema,
  definitions,
  depth = 0
}: {
  schema: StructuredValueSchema;
  definitions: readonly StructuredValueSchemaDefinition[];
  depth?: number;
}) {
  const copy = useCopy();
  return (
    <ul className="apf-value-schema-tree">
      {schema.properties.map((property) => {
        const nested = property.valueSchema ? resolveValueSchema(property.valueSchema, definitions) : undefined;
        return (
          <li key={`${depth}-${property.id}`}>
            <div>
              <code>{property.id}</code>
              <span>{property.type}{property.format ? ` · ${property.format}` : ""}{property.listSemantics ? ` · ${property.listSemantics}` : ""}{property.referenceEntityId ? ` → ${property.referenceEntityId}` : ""}</span>
              <small>{property.required ? copy("page.groundworks.field.required-lowercase", "required") : copy("page.groundworks.field.optional-lowercase", "optional")}{property.nullable ? ` · ${copy("page.groundworks.field.nullable", "nullable")}` : ""}</small>
            </div>
            {nested && depth < 4 ? <SchemaTree schema={nested.schema} definitions={definitions} depth={depth + 1} /> : null}
          </li>
        );
      })}
    </ul>
  );
}

function ValueSchemaContract({ binding }: { binding: ValueSchemaBinding }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const resolved = resolveValueSchema(binding, snapshot.groundworks.structuredValueSchemas, copy);
  if (!resolved) return <small>{copy("page.groundworks.value-schemas.unresolved", "Unresolved value schema")}</small>;
  return (
    <details className="apf-value-schema-contract">
      <summary>{resolved.label} · {humanize(resolved.schema.container)}</summary>
      <SchemaTree schema={resolved.schema} definitions={snapshot.groundworks.structuredValueSchemas} />
    </details>
  );
}

function CodeValues({ values, empty }: { values: readonly string[]; empty?: string }) {
  const copy = useCopy();
  if (!values.length) return <span>{empty ?? copy("page.groundworks.contract.none-declared", "None declared")}</span>;
  return <span className="apf-field-chip-list">{values.map((value) => <code key={value}>{value}</code>)}</span>;
}

function MappingContract({ mapping }: { mapping: DataMapping }) {
  const copy = useCopy();
  return (
    <dl className="apf-definition-list apf-mapping-contract">
      <div><dt>{copy("page.groundworks.platform-mappings.contract.ownership", "Ownership")}</dt><dd>{humanize(mapping.ownershipMode)} · {humanize(mapping.access)}</dd></div>
      <div><dt>{copy("page.groundworks.platform-mappings.contract.interface", "Interface")}</dt><dd><code>{mapping.interface}</code></dd></div>
      <div><dt>{copy("page.groundworks.platform-mappings.contract.operation", "Operation")}</dt><dd><code>{mapping.operationId}</code></dd></div>
      <div><dt>{copy("page.groundworks.platform-mappings.contract.mapped-fields", "Mapped fields")}</dt><dd><CodeValues values={mapping.fieldIds} /></dd></div>
      <div><dt>{copy("page.groundworks.platform-mappings.contract.record-keys", "Record keys")}</dt><dd><CodeValues values={mapping.keyFieldIds} /></dd></div>
      <div><dt>{copy("page.groundworks.platform-mappings.contract.concurrency", "Concurrency")}</dt><dd>{humanize(mapping.concurrency)} {copy("page.groundworks.platform-mappings.contract.concurrency-through", "through")} <code>{mapping.concurrencyFieldId}</code></dd></div>
      <div><dt>{copy("page.groundworks.platform-mappings.contract.authority", "Authority")}</dt><dd><code>{mapping.authorityFieldId}</code></dd></div>
      <div><dt>{copy("page.groundworks.platform-mappings.contract.idempotency", "Idempotency")}</dt><dd><CodeValues values={mapping.idempotencyKeyFieldIds} empty={copy("page.groundworks.platform-mappings.contract.no-idempotency-key", "No idempotency key")} /></dd></div>
      <div><dt>{copy("page.groundworks.platform-mappings.contract.reconciliation", "Reconciliation")}</dt><dd><CodeValues values={mapping.reconciliationKeyFieldIds} empty={copy("page.groundworks.platform-mappings.contract.no-reconciliation-key", "No reconciliation key")} /></dd></div>
      <div><dt>{copy("page.groundworks.platform-mappings.contract.last-good", "Last-good")}</dt><dd>{mapping.preserveLastGood ? copy("page.groundworks.platform-mappings.contract.last-good-preserved", "Preserve the last successfully reconciled state") : copy("page.groundworks.platform-mappings.contract.last-good-absent", "No last-good state is retained by this operation")}</dd></div>
      <div><dt>{copy("page.groundworks.platform-mappings.contract.envelope", "Envelope")}</dt><dd><code>v{mapping.recordEnvelopeVersion}</code></dd></div>
    </dl>
  );
}

function envelopeRole(envelope: LedgerRecordEnvelopeDefinition, fieldId: string, copy: CopyResolver) {
  const roles: Array<[string, string]> = [
    [envelope.businessKeyFieldId, copy("page.groundworks.record-envelope.role.business-key", "Business key")],
    [envelope.concurrencyFieldId, copy("page.groundworks.record-envelope.role.concurrency", "Concurrency")],
    [envelope.schemaVersionFieldId, copy("page.groundworks.record-envelope.role.schema-compatibility", "Schema compatibility")],
    [envelope.lifecycleFieldId, copy("page.groundworks.record-envelope.role.lifecycle-state", "Lifecycle state")],
    [envelope.effectiveAtFieldId, copy("page.groundworks.record-envelope.role.business-effective-time", "Business-effective time")],
    [envelope.freshnessFieldId, copy("page.groundworks.record-envelope.role.freshness", "Freshness")],
    [envelope.lastGoodAtFieldId, copy("page.groundworks.record-envelope.role.last-good-preservation", "Last-good preservation")],
    [envelope.writerVersionFieldId, copy("page.groundworks.record-envelope.role.writer-release", "Writer release")],
    [envelope.authorityFieldId, copy("page.groundworks.record-envelope.role.authority", "Authority")],
    [envelope.provenanceFieldId, copy("page.groundworks.record-envelope.role.provenance", "Provenance")]
  ];
  return roles.find(([id]) => id === fieldId)?.[1] ?? copy("page.groundworks.record-envelope.role.composed-metadata", "Composed metadata");
}

function fieldSearchText(
  entity: DataEntityDefinition,
  field: DataEntityDefinition["fields"][number],
  schemas: readonly StructuredValueSchemaDefinition[],
  copy: CopyResolver,
  operationSearchText = ""
) {
  return [
    entity.id,
    entity.label,
    field.id,
    field.label,
    field.definition,
    field.type,
    field.format ?? "",
    field.classification,
    sourceAuthorityLabel(field, copy),
    field.referenceEntityId ?? "",
    field.listSemantics ?? "",
    field.introducedIn,
    field.deprecatedIn ?? "",
    entity.ownerServiceId ?? "",
    operationSearchText,
    ...(field.enumValues ?? []),
    ...field.standardMappings.flatMap((mapping) => [
      mapping.standardId,
      mapping.releaseId,
      mapping.mappingKind,
      mapping.documentId ?? "",
      mapping.conceptId ?? "",
      mapping.path,
      mapping.direction,
      mapping.coverage,
      mapping.note ?? "",
      ...(mapping.components ?? []).flatMap((component) => [component.valuePath, component.standardPath])
    ]),
    field.valueSchema
      ? schemaSearchText(resolveValueSchema(field.valueSchema, schemas)?.schema ?? { container: "object", additionalProperties: false, properties: [] }, schemas)
      : ""
  ].join(" ").toLowerCase();
}

function allEntityConsumers(snapshot: ReturnType<typeof useFramework>["snapshot"], entity: DataEntityDefinition) {
  const consumers = new Set(entity.consumerBusinessModuleIds);
  for (const businessModule of snapshot.businessModuleRegistry.businessModules) {
    if (businessModule.dataMappings.some((mapping) => mapping.entityId === entity.id)) consumers.add(businessModule.id);
  }
  return [...consumers].sort();
}

function EntityDetail({ entity }: { entity: DataEntityDefinition }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const domains = new Map(snapshot.content.domainWorkblocks.map((workblock) => [workblock.id, copy(workblock.labelKey)]));
  const businessModules = new Map(snapshot.businessModuleRegistry.businessModules.map((module) => [module.id, module]));
  const ownerService = entity.ownerServiceId
    ? snapshot.groundworks.platformServices.find((service) => service.id === entity.ownerServiceId)
    : undefined;
  const ownerBusinessModule = entity.ownerBusinessModuleId ? businessModules.get(entity.ownerBusinessModuleId) : undefined;
  const ownerLabel = ownerService?.label ?? (ownerBusinessModule ? copy(ownerBusinessModule.labelKey) : undefined) ?? entity.ownerServiceId ?? entity.ownerBusinessModuleId ?? copy("page.groundworks.entity.owner-unresolved", "Owner not resolved");
  const ownerKind = ownerService ? copy("page.groundworks.entity.owner-kind.platform-service", "Platform service owner") : copy("page.groundworks.entity.owner-kind.business-module", "Business module owner");
  const consumers = allEntityConsumers(snapshot, entity);
  const inbound = snapshot.groundworks.entities.flatMap((source) =>
    source.relationships
      .filter((relationship) => relationship.targetEntityId === entity.id)
      .map((relationship) => ({ source, relationship }))
  );
  const mappings = entity.fields.flatMap((field) => field.standardMappings.map((mapping) => ({ field, mapping })));

  return (
    <div className="apf-page apf-register-reference apf-data-model">
      <Link className="apf-button apf-button--quiet apf-back-link" to="/groundworks">
        {copy("page.groundworks.entity.back-link", "Back to the data model")}
      </Link>
      <PlaceTrail stops={[
        { label: copy("page.groundworks.entity.trail.framework", "Framework"), to: "/" },
        { label: copy("page.groundworks.title"), to: "/groundworks" },
        { label: entity.label }
      ]} />
      <PageHeader
        eyebrow={`${humanize(entity.kind)} · ${entity.id}`}
        title={entity.label}
        description={entity.definition}
        meta={<span>{copy("page.groundworks.entity.schema-version-label", "Entity schema")} v{entity.version}</span>}
      />

      <FactBand
        label={copy("page.groundworks.entity.facts.label", "Entity contract readings")}
        facts={[
          { label: copy("page.groundworks.entity.facts.payload-fields", "Payload fields"), reading: entity.fields.length },
          { label: copy("page.groundworks.entity.facts.outbound-relationships", "Outbound relationships"), reading: entity.relationships.length },
          { label: copy("page.groundworks.entity.facts.standards-mappings", "Standards mappings"), reading: mappingCount(entity) },
          { label: copy("page.groundworks.entity.facts.workblock-consumers", "Workblock consumers"), reading: consumers.length },
          { label: copy("page.groundworks.entity.facts.record-envelope", "Record envelope"), reading: `v${snapshot.groundworks.recordEnvelope.version}` }
        ]}
      />

      <div className="apf-groundworks-detail-grid">
        <section className="apf-surface apf-groundworks-panel" aria-labelledby="groundworks-lifecycle-title">
          <div className="apf-panel-header">
            <div>
              <p className="apf-section-heading__eyebrow">{copy("page.groundworks.entity.lifecycle.eyebrow", "Record contract")}</p>
              <h2 id="groundworks-lifecycle-title">{copy("page.groundworks.entity.lifecycle.title", "Lifecycle and ownership")}</h2>
            </div>
          </div>
          <dl className="apf-definition-list">
            <div><dt>{copy("page.groundworks.entity.lifecycle.domain-workblocks", "Domain Workblocks")}</dt><dd>{entity.domainWorkblockIds.map((id) => domains.get(id) ?? id).join(", ")}</dd></div>
            <div><dt>{copy("page.groundworks.entity.lifecycle.storage-class", "Storage class")}</dt><dd>{humanize(entity.storageClass)}</dd></div>
            {entity.platformTargets.length ? (
              <div>
                <dt>{copy("page.groundworks.entity.lifecycle.platform-targets", "Platform targets")}</dt>
                <dd>{entity.platformTargets.map((target) => `${humanize(target.id)} · ${humanize(target.status)}`).join(", ")}</dd>
              </div>
            ) : null}
            <div><dt>{copy("page.groundworks.entity.lifecycle.primary-key", "Primary key")}</dt><dd><code>{entity.primaryKey}</code></dd></div>
            <div>
              <dt>{ownerKind}</dt>
              <dd>
                {ownerBusinessModule
                  ? <Link to={businessModulePath(ownerBusinessModule.domainWorkblockId, ownerBusinessModule.id)}>{ownerLabel}</Link>
                  : <Link to="/groundworks#platform-services">{ownerLabel}</Link>}
              </dd>
            </div>
            {ownerService ? <div><dt>{copy("page.groundworks.entity.lifecycle.ownership-boundary", "Ownership boundary")}</dt><dd>{ownerService.ownershipBoundary}</dd></div> : null}
            <div><dt>{copy("page.groundworks.entity.lifecycle.retention", "Retention")}</dt><dd>{humanize(entity.retentionClass)}</dd></div>
            <div><dt>{copy("page.groundworks.entity.lifecycle.record-envelope", "Record envelope")}</dt><dd><Link to="/groundworks#record-envelope"><code>{entity.recordEnvelopeId}</code> · v{snapshot.groundworks.recordEnvelope.version}</Link></dd></div>
            <div><dt>{copy("page.groundworks.entity.lifecycle.lifecycle", "Lifecycle")}</dt><dd>{entity.lifecycle.map(humanize).join(" → ")}</dd></div>
            <div><dt>{copy("page.groundworks.entity.lifecycle.freshness-contract", "Freshness contract")}</dt><dd>{freshnessContractLabel(entity, copy)}</dd></div>
            <div><dt>{copy("page.groundworks.entity.lifecycle.provenance-contract", "Provenance contract")}</dt><dd>{provenanceContractLabel(entity, copy)}</dd></div>
            <div><dt>{copy("page.groundworks.entity.lifecycle.access-contract", "Access contract")}</dt><dd>{accessContractLabel(entity, copy)}</dd></div>
            <div><dt>{copy("page.groundworks.entity.lifecycle.source-system-roles", "Source-system roles")}</dt><dd>{entity.sourceSystemRoleIds.map(humanize).join(", ") || copy("page.groundworks.contract.none-declared", "None declared")}</dd></div>
            <div><dt>{copy("page.groundworks.entity.lifecycle.writeback-system-roles", "Writeback-system roles")}</dt><dd>{entity.writebackSystemRoleIds.map(humanize).join(", ") || copy("page.groundworks.entity.lifecycle.no-writeback", "No writeback")}</dd></div>
          </dl>
        </section>

        <section className="apf-surface apf-groundworks-panel" aria-labelledby="groundworks-consumers-title">
          <div className="apf-panel-header">
            <div>
              <p className="apf-section-heading__eyebrow">{copy("page.groundworks.entity.consumers.eyebrow", "Connected work")}</p>
              <h2 id="groundworks-consumers-title">{copy("page.groundworks.entity.consumers.title", "Consumers and lookup paths")}</h2>
            </div>
          </div>
          <h3>{copy("page.groundworks.entity.consumers.business-modules-title", "Business module consumers")}</h3>
          {consumers.length ? (
            <ul className="apf-consumer-list">
              {consumers.map((id) => {
                const businessModule = businessModules.get(id);
                return (
                  <li key={id}>
                    <Link to={businessModule ? businessModulePath(businessModule.domainWorkblockId, id) : "/groundworks"}><span><strong>{businessModule ? copy(businessModule.labelKey) : id}</strong><code>{id}</code></span>{businessModule ? <StatusMark value={businessModule.implementationStatus} /> : null}<ArrowRight size={16} aria-hidden="true" /></Link>
                  </li>
                );
              })}
            </ul>
          ) : <p>{copy("page.groundworks.entity.consumers.empty", "No business module consumers declared.")}</p>}
          <h3>{copy("page.groundworks.entity.consumers.indexes-title", "Indexes")}</h3>
          <ul className="apf-code-list">
            {entity.indexes.map((index) => (
              <li key={index.id}>
                <code>{index.fieldIds.join(" + ")}</code>
                {index.unique ? ` · ${copy("page.groundworks.entity.consumers.index-unique", "unique")}` : ""}
              </li>
            ))}
          </ul>
          <h3>{copy("page.groundworks.entity.consumers.search-terms-title", "Search terms")}</h3>
          <p>{entity.searchTerms.join(", ")}</p>
        </section>
      </div>

      <section className="apf-page-section" aria-labelledby="groundworks-fields-title">
        <div className="apf-section-heading">
          <div>
            <p className="apf-section-heading__eyebrow">{copy("page.groundworks.entity.fields.eyebrow", "Exact model")}</p>
            <h2 id="groundworks-fields-title">{copy("page.groundworks.entity.fields.title", "Payload field contracts")}</h2>
            <p>{copy("page.groundworks.entity.fields.description-lead", "Requiredness, classification, source authority and external mappings are explicit per payload field. The")} <Link to="/groundworks#record-envelope">{copy("page.groundworks.entity.fields.description-link", "shared record envelope")}</Link> {copy("page.groundworks.entity.fields.description-tail", "is composed into every canonical record.")}</p>
          </div>
        </div>
        <div className="apf-surface apf-data-table-wrap" role="region" aria-label={`${copy("page.groundworks.entity.fields.table-label", "Payload fields for")} ${entity.label}`} tabIndex={0}>
          <table className="apf-data-table apf-data-table--responsive apf-groundworks-fields">
            <caption className="apf-visually-hidden">{copy("page.groundworks.entity.fields.table-label", "Payload fields for")} {entity.label}</caption>
            <thead>
              <tr>
                <th>{copy("page.groundworks.entity.fields.column.field", "Field")}</th>
                <th>{copy("page.groundworks.entity.fields.column.type", "Type")}</th>
                <th>{copy("page.groundworks.entity.fields.column.requirement", "Requirement")}</th>
                <th>{copy("page.groundworks.entity.fields.column.classification", "Classification")}</th>
                <th>{copy("page.groundworks.entity.fields.column.definition-source", "Definition and source")}</th>
                <th>{copy("page.groundworks.entity.fields.column.version", "Version")}</th>
              </tr>
            </thead>
            <tbody>
              {entity.fields.map((field) => (
                <tr key={field.id}>
                  <td><strong>{field.label}</strong><code>{field.id}</code></td>
                  <td data-label={copy("page.groundworks.entity.fields.column.type", "Type")}>
                    <code>{field.type}{field.format ? ` · ${field.format}` : ""}{field.listSemantics ? ` · ${field.listSemantics}` : ""}</code>
                    {field.valueSchema ? <ValueSchemaContract binding={field.valueSchema} /> : null}
                  </td>
                  <td data-label={copy("page.groundworks.entity.fields.column.requirement", "Requirement")}>{field.required ? copy("page.groundworks.field.required", "Required") : copy("page.groundworks.field.optional", "Optional")}{field.nullable ? ` · ${copy("page.groundworks.field.nullable", "nullable")}` : ""}</td>
                  <td data-label={copy("page.groundworks.entity.fields.column.classification", "Classification")}><span className="apf-identity-meta">{humanize(field.classification)}</span></td>
                  <td data-label={copy("page.groundworks.entity.fields.column.definition-source", "Definition and source")}><span>{field.definition}</span><small>{copy("page.groundworks.field.source-authority-label", "Source authority:")} {sourceAuthorityLabel(field, copy)}</small>{field.referenceEntityId ? <Link to={groundworksEntityPath(field.referenceEntityId)}>{copy("page.groundworks.field.references", "References")} {field.referenceEntityId}</Link> : null}</td>
                  <td data-label={copy("page.groundworks.entity.fields.column.version", "Version")}><code>{field.introducedIn}</code>{field.deprecatedIn ? <small>{copy("page.groundworks.field.deprecated", "Deprecated")} {field.deprecatedIn}</small> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="apf-groundworks-detail-grid">
        <section className="apf-surface apf-groundworks-panel" aria-labelledby="groundworks-relations-title">
          <div className="apf-panel-header"><div><p className="apf-section-heading__eyebrow">{copy("page.groundworks.entity.relationships.eyebrow", "Graph")}</p><h2 id="groundworks-relations-title">{copy("page.groundworks.entity.relationships.title", "Relationships")}</h2></div></div>
          {entity.relationships.length || inbound.length ? (
            <ul className="apf-relationship-list">
              {entity.relationships.map((relationship) => (
                <li key={relationship.id}>
                  <span className="apf-identity-meta">{copy("page.groundworks.entity.relationships.outbound", "Outbound")}</span>
                  <strong>{relationship.definition}</strong>
                  <span><code>{relationship.sourceFieldId}</code> → <Link to={groundworksEntityPath(relationship.targetEntityId)}><code>{relationship.targetEntityId}.{relationship.targetFieldId}</code></Link></span>
                  <small>{humanize(relationship.cardinality)} · {relationship.required ? copy("page.groundworks.field.required-lowercase", "required") : copy("page.groundworks.field.optional-lowercase", "optional")} · {copy("page.groundworks.entity.relationships.since", "since")} {relationship.introducedIn}</small>
                </li>
              ))}
              {inbound.map(({ source, relationship }) => (
                <li key={`${source.id}-${relationship.id}`}>
                  <span className="apf-identity-meta">{copy("page.groundworks.entity.relationships.inbound", "Inbound")}</span>
                  <strong>{relationship.definition}</strong>
                  <span><Link to={groundworksEntityPath(source.id)}><code>{source.id}.{relationship.sourceFieldId}</code></Link> → <code>{relationship.targetFieldId}</code></span>
                  <small>{humanize(relationship.cardinality)} · {copy("page.groundworks.entity.relationships.since", "since")} {relationship.introducedIn}</small>
                </li>
              ))}
            </ul>
          ) : <p>{copy("page.groundworks.entity.relationships.empty", "No direct registry relationships declared.")}</p>}
        </section>

        <section className="apf-surface apf-groundworks-panel" aria-labelledby="groundworks-mappings-title">
          <div className="apf-panel-header"><div><p className="apf-section-heading__eyebrow">{copy("page.groundworks.entity.mappings.eyebrow", "Interoperability")}</p><h2 id="groundworks-mappings-title">{copy("page.groundworks.entity.mappings.title", "Standards mappings")}</h2></div></div>
          {mappings.length ? (
            <ul className="apf-mapping-list">
              {mappings.map(({ field, mapping }, index) => (
                <li key={`${field.id}-${mapping.standardId}-${index}`}>
                  <div><strong>{field.label}</strong><span className="apf-identity-meta">{humanize(mapping.coverage)}</span></div>
                  <code>{mapping.standardId} · {standardMappingTarget(mapping)}</code>
                  <span>{mapping.path}</span>
                  {mapping.components?.length ? (
                    <ul className="apf-code-list">
                      {mapping.components.map((component) => <li key={`${component.valuePath}-${component.standardPath}`}><code>{component.valuePath}</code> → <code>{component.standardPath}</code></li>)}
                    </ul>
                  ) : null}
                  <small>{humanize(mapping.direction)}{mapping.note ? ` · ${mapping.note}` : ""}</small>
                </li>
              ))}
            </ul>
          ) : <p>{copy("page.groundworks.entity.mappings.empty", "This internal entity has no forced external standard mapping.")}</p>}
        </section>
      </div>
    </div>
  );
}

/** Filters that apply to the entity and field sections. Every other section is small enough to read whole. */
interface RegistryFilters {
  query: string;
  domainId: string;
  dimensionIds: string[];
  kind: string;
  surface: string;
  standardId: string;
}

interface IndexEntry {
  id: string;
  label: string;
  /** Words a reader may filter the index by that the label does not use. Never rendered. */
  keywords: string;
}

/** An index entry whose label and filter words are resolved from the copy register. */
interface KeyedIndexEntry extends IndexEntry {
  labelKey: string;
  keywordsKey: string;
}

interface IndexGroup {
  id: string;
  label: string;
  entries: IndexEntry[];
}

const sharedSections: KeyedIndexEntry[] = [
  {
    id: "entities",
    labelKey: "page.groundworks.index.entities",
    label: "Canonical entities",
    keywordsKey: "page.groundworks.index.entities.keywords",
    keywords: "records entity owner retention surface domain relationships kind"
  },
  {
    id: "fields",
    labelKey: "page.groundworks.index.fields",
    label: "Payload fields and standards paths",
    keywordsKey: "page.groundworks.index.fields.keywords",
    keywords: "field ubl peppol path mapping classification source of truth requiredness"
  },
  {
    id: "dimensions",
    labelKey: "page.groundworks.index.dimensions",
    label: "Dimensions",
    keywordsKey: "page.groundworks.index.dimensions.keywords",
    keywords: "classification category taxonomy assignment override resolution storage direction projection"
  },
  {
    id: "record-envelope",
    labelKey: "page.groundworks.index.record-envelope",
    label: "Record envelope",
    keywordsKey: "page.groundworks.index.record-envelope.keywords",
    keywords: "authority provenance freshness concurrency last good business key writer"
  },
  {
    id: "structured-value-schemas",
    labelKey: "page.groundworks.index.structured-value-schemas",
    label: "Structured value schemas",
    keywordsKey: "page.groundworks.index.structured-value-schemas.keywords",
    keywords: "money quantity container nested closed structure version"
  },
  {
    id: "standards-registry",
    labelKey: "page.groundworks.index.standards-registry",
    label: "Standards and boundaries",
    keywordsKey: "page.groundworks.index.standards-registry.keywords",
    keywords: "ubl peppol exchange projection interoperability version"
  },
  {
    id: "registry-vocabulary",
    labelKey: "page.groundworks.index.registry-vocabulary",
    label: "Vocabulary",
    keywordsKey: "page.groundworks.index.registry-vocabulary.keywords",
    keywords: "terms aliases definitions governed language"
  },
  {
    id: "platform-services",
    labelKey: "page.groundworks.index.platform-services",
    label: "Platform services",
    keywordsKey: "page.groundworks.index.platform-services.keywords",
    keywords: "single writer ownership boundary interfaces owned records"
  },
  {
    id: "platform-mappings",
    labelKey: "page.groundworks.index.platform-mappings",
    label: "Service mappings",
    keywordsKey: "page.groundworks.index.platform-mappings.keywords",
    keywords: "operations idempotency reconciliation concurrency commands queries events"
  }
];

/** Older in-page anchors keep working: each one selects the section that now carries it. */
const legacyAnchors: Record<string, string> = {
  "groundworks-registry-title": "entities",
  "entity-registry-title": "entities",
  "field-registry": "fields",
  "field-registry-title": "fields",
  "dimensions-title": "dimensions",
  "record-envelope-title": "record-envelope",
  "structured-value-schemas-title": "structured-value-schemas",
  "standards-registry-title": "standards-registry",
  "registry-vocabulary-title": "registry-vocabulary",
  "platform-services-title": "platform-services",
  "platform-mappings-title": "platform-mappings",
  "groundworks-method-title": "overview"
};

function OverviewSection() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const fieldTotal = snapshot.groundworks.entities.reduce((total, entity) => total + entity.fields.length, 0);
  const relationshipTotal = snapshot.groundworks.entities.reduce((total, entity) => total + entity.relationships.length, 0);
  const envelope = snapshot.groundworks.recordEnvelope;

  return (
    <>
      <div className="apf-section-heading">
        <div>
          <h2 id="data-model-section-title">{copy("page.groundworks.overview.title", "One record definition per fact")}</h2>
          <p>{copy("page.groundworks.overview.description", "Every Workblock reads and writes the same canonical records. One entity owns each fact, one service or Workblock owns each entity, and every payload field states its type, its requiredness, its classification and the standard path it maps to at an exchange boundary.")}</p>
        </div>
      </div>

      <FactBand
        label={copy("page.groundworks.overview.facts.label", "Model size")}
        facts={[
          { label: copy("page.groundworks.overview.facts.entities", "Canonical entities"), reading: snapshot.groundworks.entities.length },
          { label: copy("page.groundworks.overview.facts.fields", "Payload fields"), reading: fieldTotal },
          { label: copy("page.groundworks.overview.facts.relationships", "Relationships"), reading: relationshipTotal },
          { label: copy("page.groundworks.overview.facts.envelope-fields", "Envelope fields"), reading: envelope.fields.length },
          { label: copy("page.groundworks.overview.facts.value-schemas", "Value schemas"), reading: snapshot.groundworks.structuredValueSchemas.length },
          { label: copy("page.groundworks.overview.facts.vocabulary-terms", "Vocabulary terms"), reading: snapshot.groundworks.vocabulary.length },
          { label: copy("page.groundworks.overview.facts.platform-owners", "Platform owners"), reading: snapshot.groundworks.platformServices.length },
          { label: copy("page.groundworks.overview.facts.service-mappings", "Service mappings"), reading: snapshot.groundworks.platformDataMappings.length }
        ]}
      />

      <div className="apf-ledger-columns apf-ledger-columns--three">
        <article className="apf-ledger-column">
          <p className="apf-ledger-column__eyebrow">{copy("page.groundworks.overview.column.versioning.eyebrow", "Versioned meaning")}</p>
          <h3>{copy("page.groundworks.overview.column.versioning.title", "Version every meaning")}</h3>
          <p>{"versioningPolicy" in snapshot.groundworks ? snapshot.groundworks.versioningPolicy : snapshot.groundworks.changePolicy}</p>
        </article>
        <article className="apf-ledger-column">
          <p className="apf-ledger-column__eyebrow">{copy("page.groundworks.overview.column.ownership.eyebrow", "Ownership")}</p>
          <h3>{copy("page.groundworks.overview.column.ownership.title", "One owner surface")}</h3>
          <p>{copy("page.groundworks.overview.column.ownership.body", "Canonical mutable records, run analytics, binaries, retrieval indexes, retry progress, events and learning examples stay on distinct surfaces.")}</p>
        </article>
        <article className="apf-ledger-column">
          <p className="apf-ledger-column__eyebrow">{copy("page.groundworks.overview.column.interoperability.eyebrow", "Interoperability")}</p>
          <h3>{copy("page.groundworks.overview.column.interoperability.title", "Map, do not flatten")}</h3>
          <p>{copy("page.groundworks.overview.column.interoperability.body", "UBL and other standards describe exchange projections. Internal authority, evidence, learning and lifecycle records stay explicit platform contracts.")}</p>
        </article>
      </div>
    </>
  );
}

function EntitiesSection({ filters }: { filters: RegistryFilters }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const normalized = filters.query.trim().toLowerCase();
  const dimensions = useMemo(() => resolveGroundworksDimensions(snapshot, copy), [copy, snapshot]);
  const dimensionsById = useMemo(() => new Map(dimensions.map((dimension) => [dimension.id, dimension])), [dimensions]);
  const domainNames = new Map(snapshot.content.domainWorkblocks.map((workblock) => [workblock.id, copy(workblock.shortLabelKey)]));
  const businessModuleNames = new Map(snapshot.businessModuleRegistry.businessModules.map((module) => [module.id, copy(module.labelKey)]));
  const platformServiceNames = new Map(snapshot.groundworks.platformServices.map((service) => [service.id, service.label]));
  const platformMappingsByEntity = new Map<string, PlatformDataMapping[]>();
  for (const mapping of snapshot.groundworks.platformDataMappings) {
    const mappings = platformMappingsByEntity.get(mapping.entityId) ?? [];
    mappings.push(mapping);
    platformMappingsByEntity.set(mapping.entityId, mappings);
  }

  const filtered = snapshot.groundworks.entities.filter((entity) => {
    const standards = entity.fields.flatMap((field) => field.standardMappings.map((mapping) => mapping.standardId));
    const searchText = [
      entity.id,
      entity.label,
      entity.pluralLabel,
      entity.definition,
      entity.kind,
      entity.storageClass,
      entity.ownerBusinessModuleId,
      entity.ownerServiceId,
      entity.recordEnvelopeId,
      entity.ownerBusinessModuleId ? businessModuleNames.get(entity.ownerBusinessModuleId) : "",
      entity.ownerServiceId ? platformServiceNames.get(entity.ownerServiceId) : "",
      ...entity.searchTerms,
      ...entity.domainWorkblockIds,
      dimensionSearchTextForEntity(entity, dimensions),
      ...standards,
      ...entity.fields.flatMap((field) => [
        field.id,
        field.label,
        field.definition,
        sourceAuthorityLabel(field, copy),
        field.referenceEntityId ?? "",
        field.listSemantics ?? "",
        ...field.standardMappings.flatMap((mapping) => [
          mapping.standardId,
          mapping.releaseId,
          mapping.mappingKind,
          mapping.documentId ?? "",
          mapping.conceptId ?? "",
          mapping.path,
          mapping.direction,
          mapping.coverage,
          mapping.note ?? "",
          ...(mapping.components ?? []).flatMap((component) => [component.valuePath, component.standardPath])
        ]),
        field.valueSchema
          ? schemaSearchText(resolveValueSchema(field.valueSchema, snapshot.groundworks.structuredValueSchemas)?.schema ?? { container: "object", additionalProperties: false, properties: [] }, snapshot.groundworks.structuredValueSchemas)
          : ""
      ]),
      ...entity.relationships.flatMap((relationship) => [relationship.id, relationship.definition, relationship.targetEntityId]),
      ...(platformMappingsByEntity.get(entity.id) ?? []).flatMap((mapping) => [
        mapping.serviceId,
        platformServiceNames.get(mapping.serviceId) ?? "",
        mapping.operationId,
        mapping.interface,
        mapping.purpose
      ])
    ].join(" ").toLowerCase();
    return (!normalized || searchText.includes(normalized)) &&
      (filters.domainId === "all" || entity.domainWorkblockIds.includes(filters.domainId)) &&
      dimensionMatchesEntity(entity, filters.dimensionIds, dimensionsById) &&
      (filters.kind === "all" || entity.kind === filters.kind) &&
      (filters.surface === "all" || entity.storageClass === filters.surface) &&
      (filters.standardId === "all" || standards.includes(filters.standardId));
  });

  return (
    <>
      <div className="apf-section-heading">
        <div>
          <h2 id="data-model-section-title">
            <Glyph meaning="class.record" size={20} className="apf-heading-glyph" />
            {copy("page.groundworks.entities.title", "Canonical entities")}
          </h2>
          <p aria-live="polite">{filtered.length} {copy("page.groundworks.entities.count-connector", "of")} {snapshot.groundworks.entities.length} {copy("page.groundworks.entities.count-suffix", "records match. Each one opens its exact field contracts, relationships and consumers.")}</p>
        </div>
      </div>
      {filtered.length ? (
        <div className="apf-surface apf-data-table-wrap" role="region" aria-label={copy("page.groundworks.entities.table-label", "Canonical entity definitions")} tabIndex={0}>
          <table className="apf-data-table apf-data-table--responsive apf-groundworks-table">
            <caption className="apf-visually-hidden">{copy("page.groundworks.entities.table-label", "Canonical entity definitions")}</caption>
            <thead><tr><th>{copy("page.groundworks.entities.column.entity", "Entity")}</th><th>{copy("page.groundworks.entities.column.kind", "Kind")}</th><th>{copy("page.groundworks.entities.column.domains", "Domains")}</th><th>{copy("page.groundworks.entities.column.owner-surface", "Owner and surface")}</th><th data-align="numeric">{copy("page.groundworks.entities.column.fields", "Fields")}</th><th data-align="numeric">{copy("page.groundworks.entities.column.mappings", "Mappings")}</th><th>{copy("page.groundworks.entities.column.version", "Version")}</th></tr></thead>
            <tbody>
              {filtered.map((entity) => {
                const ownerName = entity.ownerServiceId
                  ? platformServiceNames.get(entity.ownerServiceId) ?? entity.ownerServiceId
                  : entity.ownerBusinessModuleId
                    ? businessModuleNames.get(entity.ownerBusinessModuleId) ?? entity.ownerBusinessModuleId
                    : copy("page.groundworks.entity.owner-unresolved", "Owner not resolved");
                return (
                  <tr key={entity.id}>
                    <td><Link className="apf-groundworks-entity-link" to={groundworksEntityPath(entity.id)}><strong>{entity.label}</strong><code>{entity.id}</code><span>{entity.definition}</span></Link></td>
                    <td data-label={copy("page.groundworks.entities.column.kind", "Kind")}><span className="apf-identity-meta">{humanize(entity.kind)}</span></td>
                    <td data-label={copy("page.groundworks.entities.column.workblocks", "Workblocks")}>{entity.domainWorkblockIds.slice(0, 2).map((id) => domainNames.get(id) ?? id).join(", ")}{entity.domainWorkblockIds.length > 2 ? ` +${entity.domainWorkblockIds.length - 2}` : ""}</td>
                    <td data-label={copy("page.groundworks.entities.column.owner-surface", "Owner and surface")}><strong>{ownerName}</strong><small>{entity.ownerServiceId ? copy("page.groundworks.entities.owner.platform-service", "Platform service") : copy("page.groundworks.entities.owner.workblock", "Workblock")} · {humanize(entity.storageClass)}</small></td>
                    <td data-label={copy("page.groundworks.entities.column.fields", "Fields")} data-align="numeric">{entity.fields.length}</td>
                    <td data-label={copy("page.groundworks.entities.column.mappings", "Mappings")} data-align="numeric">{mappingCount(entity)}</td>
                    <td data-label={copy("page.groundworks.entities.column.version", "Version")}><code>{entity.version}</code></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <EmptyState title={copy("page.groundworks.entities.empty.title", "No entities match")} description={copy("page.groundworks.entities.empty.description", "Change or clear the filters above. Shared contracts stay reachable from the index.")} />}
    </>
  );
}

function FieldsSection({ filters }: { filters: RegistryFilters }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const normalized = filters.query.trim().toLowerCase();
  const dimensions = useMemo(() => resolveGroundworksDimensions(snapshot, copy), [copy, snapshot]);
  const dimensionsById = useMemo(() => new Map(dimensions.map((dimension) => [dimension.id, dimension])), [dimensions]);
  const businessModules = new Map(snapshot.businessModuleRegistry.businessModules.map((module) => [module.id, module]));
  const platformServiceNames = new Map(snapshot.groundworks.platformServices.map((service) => [service.id, service.label]));
  const platformOperationSearchByEntity = new Map<string, string>();
  for (const mapping of snapshot.groundworks.platformDataMappings) {
    const existing = platformOperationSearchByEntity.get(mapping.entityId) ?? "";
    platformOperationSearchByEntity.set(mapping.entityId, `${existing} ${mapping.serviceId} ${platformServiceNames.get(mapping.serviceId) ?? ""} ${mapping.operationId} ${mapping.interface} ${mapping.purpose}`);
  }

  const filteredFields = useMemo(() => snapshot.groundworks.entities.flatMap((entity) => {
    if (filters.domainId !== "all" && !entity.domainWorkblockIds.includes(filters.domainId)) return [];
    if (!dimensionMatchesEntity(entity, filters.dimensionIds, dimensionsById)) return [];
    if (filters.kind !== "all" && entity.kind !== filters.kind) return [];
    if (filters.surface !== "all" && entity.storageClass !== filters.surface) return [];
    return entity.fields.flatMap((field) => {
      const fieldStandards = field.standardMappings.map((mapping) => mapping.standardId);
      if (filters.standardId !== "all" && !fieldStandards.includes(filters.standardId)) return [];
      if (normalized && !fieldSearchText(entity, field, snapshot.groundworks.structuredValueSchemas, copy, `${platformOperationSearchByEntity.get(entity.id) ?? ""} ${dimensionSearchTextForEntity(entity, dimensions)}`).includes(normalized)) return [];
      return [{ entity, field }];
    });
  }), [copy, dimensions, dimensionsById, filters.dimensionIds, filters.domainId, filters.kind, filters.standardId, filters.surface, normalized, platformOperationSearchByEntity, snapshot.groundworks.entities, snapshot.groundworks.structuredValueSchemas]);

  const visibleFieldLimit = 60;

  return (
    <>
      <div className="apf-section-heading">
        <div>
          <h2 id="data-model-section-title">{copy("page.groundworks.fields.title", "Payload fields and standards paths")}</h2>
          <p aria-live="polite">{filteredFields.length} {copy("page.groundworks.fields.count-suffix", "fields match. Each result keeps its owner, its consumer Workblocks, its definition, its functional version and its exact standard path together.")}{filteredFields.length > visibleFieldLimit ? ` ${copy("page.groundworks.fields.limit-prefix", "The first")} ${visibleFieldLimit} ${copy("page.groundworks.fields.limit-suffix", "render below: search or filter to narrow the set.")}` : ""}</p>
        </div>
      </div>
      {filteredFields.length ? (
        <div className="apf-surface apf-data-table-wrap" role="region" aria-label={copy("page.groundworks.fields.table-label", "Payload field search results")} tabIndex={0}>
          <table className="apf-data-table apf-data-table--responsive apf-groundworks-field-results">
            <caption className="apf-visually-hidden">{copy("page.groundworks.fields.table-caption", "Searchable payload field definitions and standards mappings")}</caption>
            <thead><tr><th>{copy("page.groundworks.fields.column.entity-field", "Entity and field")}</th><th>{copy("page.groundworks.fields.column.owner-consumers", "Owner and consumers")}</th><th>{copy("page.groundworks.fields.column.definition-source", "Definition and source")}</th><th>{copy("page.groundworks.fields.column.standards-mapping", "Exact standards mapping")}</th><th>{copy("page.groundworks.fields.column.contract", "Contract")}</th></tr></thead>
            <tbody>
              {filteredFields.slice(0, visibleFieldLimit).map(({ entity, field }) => {
                const ownerName = entity.ownerServiceId
                  ? platformServiceNames.get(entity.ownerServiceId) ?? entity.ownerServiceId
                  : entity.ownerBusinessModuleId
                    ? (() => { const module = businessModules.get(entity.ownerBusinessModuleId); return module ? copy(module.labelKey) : entity.ownerBusinessModuleId; })()
                    : copy("page.groundworks.entity.owner-unresolved", "Owner not resolved");
                const consumers = allEntityConsumers(snapshot, entity);
                return (
                  <tr key={`${entity.id}.${field.id}`}>
                    <td><Link className="apf-groundworks-entity-link" to={groundworksEntityPath(entity.id)}><strong>{entity.label}</strong><code>{entity.id}.{field.id}</code><span>{field.label}</span></Link></td>
                    <td data-label={copy("page.groundworks.fields.column.owner-consumers", "Owner and consumers")}><strong>{ownerName}</strong><small>{entity.ownerServiceId ? copy("page.groundworks.entity.owner-kind.platform-service", "Platform service owner") : copy("page.groundworks.entity.owner-kind.business-module", "Business module owner")} · {humanize(entity.storageClass)}</small><div className="apf-field-consumers">{consumers.map((id) => { const module = businessModules.get(id); return <Link key={id} to={module ? businessModulePath(module.domainWorkblockId, id) : "/groundworks"}>{module ? copy(module.labelKey) : id}{module ? <StatusMark value={module.implementationStatus} /> : null}</Link>; })}</div></td>
                    <td data-label={copy("page.groundworks.fields.column.definition-source", "Definition and source")}><span>{field.definition}</span><small>{copy("page.groundworks.field.source-authority-label", "Source authority:")} {sourceAuthorityLabel(field, copy)}</small></td>
                    <td data-label={copy("page.groundworks.fields.column.standards-mapping", "Exact standards mapping")}>
                      {field.standardMappings.length ? <ul className="apf-field-mapping-paths">{field.standardMappings.map((mapping, mappingIndex) => <li key={`${mapping.standardId}-${mapping.path}-${mappingIndex}`}><span><code>{mapping.standardId}</code> · {standardMappingTarget(mapping)} · {humanize(mapping.coverage)}</span><code>{mapping.path}</code>{mapping.components?.map((component) => <small key={`${component.valuePath}-${component.standardPath}`}><code>{component.valuePath}</code> → <code>{component.standardPath}</code></small>)}</li>)}</ul> : <span>{copy("page.groundworks.fields.no-mapping", "Internal field; no external mapping declared.")}</span>}
                    </td>
                    <td data-label={copy("page.groundworks.fields.column.contract", "Contract")}><code>{field.type}{field.format ? ` · ${field.format}` : ""}</code><small>{field.required ? copy("page.groundworks.field.required", "Required") : copy("page.groundworks.field.optional", "Optional")}{field.nullable ? ` · ${copy("page.groundworks.field.nullable", "nullable")}` : ""} · {humanize(field.classification)}</small><small>{copy("page.groundworks.fields.entity-version-label", "Entity")} v{entity.version} · {copy("page.groundworks.fields.introduced-label", "introduced")} {field.introducedIn}</small></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <EmptyState title={copy("page.groundworks.fields.empty.title", "No payload fields match")} description={copy("page.groundworks.fields.empty.description", "Change the search or the filters to find an exact field, definition, source or standards path.")} />}
    </>
  );
}

function RecordEnvelopeSection() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const envelope = snapshot.groundworks.recordEnvelope;

  return (
    <>
      <div className="apf-section-heading">
        <div>
          <p className="apf-section-heading__eyebrow">{copy("page.groundworks.record-envelope.eyebrow", "Shared ledger contract")} · v{envelope.version}</p>
          <h2 id="data-model-section-title">{humanize(envelope.id)}</h2>
          <p>{envelope.definition}</p>
        </div>
      </div>
      <article className="apf-pattern-system apf-surface">
        <h3>{copy("page.groundworks.record-envelope.composition-title", "Composition rule")}</h3>
        <p>{envelope.appliesTo}</p>
      </article>
      <div className="apf-surface apf-data-table-wrap" role="region" aria-label={copy("page.groundworks.record-envelope.table-label", "Record envelope fields")} tabIndex={0}>
        <table className="apf-data-table apf-data-table--responsive apf-groundworks-fields">
          <caption className="apf-visually-hidden">{copy("page.groundworks.record-envelope.table-caption", "Fields composed into the shared ledger record envelope")}</caption>
          <thead><tr><th>{copy("page.groundworks.record-envelope.column.field", "Envelope field")}</th><th>{copy("page.groundworks.record-envelope.column.role", "Contract role")}</th><th>{copy("page.groundworks.record-envelope.column.type", "Type")}</th><th>{copy("page.groundworks.record-envelope.column.requirement", "Requirement")}</th><th>{copy("page.groundworks.record-envelope.column.definition-source", "Definition and source")}</th><th>{copy("page.groundworks.record-envelope.column.version", "Version")}</th></tr></thead>
          <tbody>
            {envelope.fields.map((field) => (
              <tr key={field.id}>
                <td><strong>{field.label}</strong><code>{field.id}</code></td>
                <td data-label={copy("page.groundworks.record-envelope.column.role", "Contract role")}>{envelopeRole(envelope, field.id, copy)}</td>
                <td data-label={copy("page.groundworks.record-envelope.column.type", "Type")}><code>{field.type}{field.format ? ` · ${field.format}` : ""}</code></td>
                <td data-label={copy("page.groundworks.record-envelope.column.requirement", "Requirement")}>{field.required ? copy("page.groundworks.field.required", "Required") : copy("page.groundworks.field.optional", "Optional")}{field.nullable ? ` · ${copy("page.groundworks.field.nullable", "nullable")}` : ""}</td>
                <td data-label={copy("page.groundworks.record-envelope.column.definition-source", "Definition and source")}><span>{field.definition}</span><small>{copy("page.groundworks.field.source-authority-label", "Source authority:")} {sourceAuthorityLabel(field, copy)}</small></td>
                <td data-label={copy("page.groundworks.record-envelope.column.version", "Version")}><code>{field.introducedIn}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function DimensionsSection() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const dimensions = useMemo(() => resolveGroundworksDimensions(snapshot, copy), [copy, snapshot]);
  const classificationAnchors = useMemo(() => resolveClassificationAnchors(snapshot), [snapshot]);

  return (
    <>
      <div className="apf-section-heading">
        <div>
          <h2 id="data-model-section-title">{copy("page.groundworks.dimensions.title", "Dimensions")}</h2>
          <p>{copy("page.groundworks.dimensions.description", "Classification dimensions keep spend segment, buying mode, accounting treatment, category and derived tail rules separate. The reference names their carriers and resolution rules without exposing tenant category values.")}</p>
        </div>
      </div>

      {dimensions.length ? (
        <div
          className="apf-surface apf-data-table-wrap"
          role="region"
          aria-label={copy("page.groundworks.dimensions.table-label", "Classification dimensions table")}
          tabIndex={0}
        >
          <table className="apf-data-table apf-data-table--responsive apf-groundworks-fields apf-groundworks-dimensions">
            <caption className="apf-visually-hidden">{copy("page.groundworks.dimensions.table-caption", "GroundWorks classification dimensions and their carriers")}</caption>
            <thead>
              <tr>
                <th>{copy("page.groundworks.dimensions.column.dimension", "Dimension")}</th>
                <th>{copy("page.groundworks.dimensions.column.members", "Members")}</th>
                <th>{copy("page.groundworks.dimensions.column.applies-to", "Applies to")}</th>
                <th>{copy("page.groundworks.dimensions.column.assignment", "Assignment")}</th>
                <th>{copy("page.groundworks.dimensions.column.override", "Override")}</th>
                <th>{copy("page.groundworks.dimensions.column.resolution", "Resolution")}</th>
                <th>{copy("page.groundworks.dimensions.column.value-source", "Value source")}</th>
              </tr>
            </thead>
            <tbody>
              {dimensions.map((dimension) => (
                <tr key={dimension.id}>
                  <td>
                    <strong>{dimension.label}</strong>
                    <code>{dimension.id}</code>
                    <span>{dimension.definition}</span>
                    <small>{copy("page.groundworks.dimensions.owner-label", "Owner:")} {dimension.owner}</small>
                  </td>
                  <td data-label={copy("page.groundworks.dimensions.column.members", "Members")}><CodeValues values={dimension.members} empty={dimension.membersEmptyLabel} /></td>
                  <td data-label={copy("page.groundworks.dimensions.column.applies-to", "Applies to")}><CodeValues values={dimension.applicabilityTargets} empty={copy("page.groundworks.dimensions.empty.applicability", "No applicability target")} /></td>
                  <td data-label={copy("page.groundworks.dimensions.column.assignment", "Assignment")}><CodeValues values={dimension.assignmentCarrierEntityIds} empty={copy("page.groundworks.dimensions.empty.assignment", "No assignment carrier")} /></td>
                  <td data-label={copy("page.groundworks.dimensions.column.override", "Override")}><CodeValues values={dimension.overrideCarrierEntityIds} empty={copy("page.groundworks.dimensions.empty.override", "No override carrier")} /></td>
                  <td data-label={copy("page.groundworks.dimensions.column.resolution", "Resolution")}><CodeValues values={[dimension.resolutionRuleId]} empty={copy("page.groundworks.dimensions.empty.resolution", "No resolution rule")} /></td>
                  <td data-label={copy("page.groundworks.dimensions.column.value-source", "Value source")}><CodeValues values={[dimension.valueSource]} empty={copy("page.groundworks.dimensions.empty.value-source", "No value source")} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          title={copy("page.groundworks.dimensions.empty.title", "No classification dimensions are available")}
          description={copy("page.groundworks.dimensions.empty.description", "Dimension definitions are not available in the data-model reference.")}
        />
      )}

      <div className="apf-section-heading">
        <div>
          <h3>{copy("page.groundworks.dimensions.anchors.title", "System classification anchors")}</h3>
          <p>{copy("page.groundworks.dimensions.anchors.description", "System classification anchors show where an external classification enters the canonical model, what it may influence and whether it is a default, committed value, proposal signal or discovery-only input. They do not indicate an installed integration.")}</p>
        </div>
      </div>

      {classificationAnchors.length ? (
        <div className="apf-surface apf-data-table-wrap" role="region" aria-label={copy("page.groundworks.dimensions.anchors.table-label", "Projected system classification anchors")} tabIndex={0}>
          <table className="apf-data-table apf-data-table--responsive apf-groundworks-fields">
            <caption className="apf-visually-hidden">{copy("page.groundworks.dimensions.anchors.table-label", "Projected system classification anchors")}</caption>
            <thead>
              <tr>
                <th>{copy("page.groundworks.dimensions.anchors.column.system", "System")}</th>
                <th>{copy("page.groundworks.dimensions.anchors.column.anchor", "Anchor")}</th>
                <th>{copy("page.groundworks.dimensions.anchors.column.canonical-target", "Canonical target")}</th>
                <th>{copy("page.groundworks.dimensions.anchors.column.authority-effects", "Authority and effects")}</th>
              </tr>
            </thead>
            <tbody>
              {classificationAnchors.flatMap((group) => group.anchors.map((anchor) => (
                <tr key={`${group.systemId}-${anchor.id}`}>
                  <td>
                    <strong>{group.systemLabel}</strong>
                    <code>{group.systemId}</code>
                  </td>
                  <td data-label={copy("page.groundworks.dimensions.anchors.column.anchor", "Anchor")}>
                    <strong>{anchor.role}</strong>
                    <span>{anchor.objectLabel} · {anchor.attributeLabel}</span>
                    {anchor.standardIds.length ? <small>{copy("page.groundworks.dimensions.anchors.standards-label", "Standards:")} {anchor.standardIds.join(", ")}</small> : null}
                  </td>
                  <td data-label={copy("page.groundworks.dimensions.anchors.column.canonical-target", "Canonical target")}><code>{anchor.canonicalTarget}</code></td>
                  <td data-label={copy("page.groundworks.dimensions.anchors.column.authority-effects", "Authority and effects")}>
                    <span>{anchor.authority}</span>
                    {anchor.businessEffectIds.length ? <small>{copy("page.groundworks.dimensions.anchors.effects-label", "Effects:")} {anchor.businessEffectIds.join(", ")}</small> : null}
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="apf-surface apf-groundworks-panel">{copy("page.groundworks.dimensions.anchors.empty", "No system classification anchors are available in this reference.")}</p>
      )}
    </>
  );
}

function ValueSchemasSection() {
  const { snapshot } = useFramework();
  const copy = useCopy();

  return (
    <>
      <div className="apf-section-heading">
        <div>
          <h2 id="data-model-section-title">{copy("page.groundworks.value-schemas.title", "Structured value schemas")}</h2>
          <p>{copy("page.groundworks.value-schemas.description", "Version-pinned money and quantity structures keep opaque object fields out of the model and make standard component mappings testable.")}</p>
        </div>
      </div>
      <div className="apf-groundworks-detail-grid">
        {snapshot.groundworks.structuredValueSchemas.map((definition) => (
          <article className="apf-surface apf-groundworks-panel" key={`${definition.id}-${definition.version}`}>
            <div className="apf-panel-header"><div><p className="apf-section-heading__eyebrow">{humanize(definition.schema.container)} · v{definition.version}</p><h3>{definition.label}</h3><code>{definition.id}</code></div></div>
            <p>{definition.definition}</p>
            <SchemaTree schema={definition.schema} definitions={snapshot.groundworks.structuredValueSchemas} />
          </article>
        ))}
      </div>
    </>
  );
}

function StandardsSection({ filters }: { filters: RegistryFilters }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const standards = snapshot.groundworks.standards.filter((standard) => filters.standardId === "all" || standard.id === filters.standardId);

  return (
    <>
      <div className="apf-section-heading">
        <div>
          <h2 id="data-model-section-title">{copy("page.groundworks.standards.title", "Standards and mapping boundaries")}</h2>
          <p>{copy("page.groundworks.standards.description", "Standards define exchange projections at named boundaries. They do not replace the platform's authority, evidence, learning or lifecycle records.")}</p>
        </div>
      </div>
      <div className="apf-surface apf-data-table-wrap" role="region" aria-label={copy("page.groundworks.standards.table-label", "Standards used by the data model")} tabIndex={0}>
        <table className="apf-data-table apf-data-table--responsive apf-groundworks-standards">
          <caption className="apf-visually-hidden">{copy("page.groundworks.standards.table-label", "Standards used by the data model")}</caption>
          <thead><tr><th>{copy("page.groundworks.standards.column.standard", "Standard")}</th><th>{copy("page.groundworks.standards.column.version", "Functional version")}</th><th>{copy("page.groundworks.standards.column.role", "Role at the boundary")}</th></tr></thead>
          <tbody>{standards.map((standard) => <tr key={standard.id}><td><strong>{standard.label}</strong><code>{standard.id}</code></td><td data-label={copy("page.groundworks.standards.column.version", "Functional version")}><code>{standard.version}</code><small>{humanize(standard.status)}</small></td><td data-label={copy("page.groundworks.standards.column.role", "Role at the boundary")}>{standard.scope}</td></tr>)}</tbody>
        </table>
      </div>
    </>
  );
}

function VocabularySection() {
  const { snapshot } = useFramework();
  const copy = useCopy();

  return (
    <>
      <div className="apf-section-heading">
        <div>
          <h2 id="data-model-section-title">{copy("page.groundworks.vocabulary.title", "Governed vocabulary")}</h2>
          <p>{copy("page.groundworks.vocabulary.description", "These terms keep authority, ownership, evidence and data contracts consistent across all nine domains.")}</p>
        </div>
      </div>
      <div className="apf-surface apf-data-table-wrap" role="region" aria-label={copy("page.groundworks.vocabulary.table-label", "Governed vocabulary definitions")} tabIndex={0}>
        <table className="apf-data-table apf-data-table--responsive apf-groundworks-fields">
          <caption className="apf-visually-hidden">{copy("page.groundworks.vocabulary.table-label", "Governed vocabulary definitions")}</caption>
          <thead><tr><th>{copy("page.groundworks.vocabulary.column.term", "Term")}</th><th>{copy("page.groundworks.vocabulary.column.definition", "Definition")}</th><th>{copy("page.groundworks.vocabulary.column.aliases", "Aliases")}</th><th>{copy("page.groundworks.vocabulary.column.version", "Version")}</th></tr></thead>
          <tbody>
            {snapshot.groundworks.vocabulary.map((entry) => (
              <tr key={entry.id}>
                <td><strong>{entry.term}</strong><code>{entry.id}</code></td>
                <td data-label={copy("page.groundworks.vocabulary.column.definition", "Definition")}>{entry.definition}</td>
                <td data-label={copy("page.groundworks.vocabulary.column.aliases", "Aliases")}>{entry.aliases.length ? entry.aliases.join(", ") : copy("page.groundworks.contract.none-declared", "None declared")}</td>
                <td data-label={copy("page.groundworks.vocabulary.column.version", "Version")}><code>{entry.introducedIn}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PlatformServicesSection() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const entityNames = new Map(snapshot.groundworks.entities.map((entity) => [entity.id, entity.label]));

  return (
    <>
      <div className="apf-section-heading">
        <div>
          <h2 id="data-model-section-title">{copy("page.groundworks.platform-services.title", "Platform services and their records")}</h2>
          <p>{copy("page.groundworks.platform-services.description", "Shared records belong to a named service. Domain Workblocks reach across that boundary through typed commands, queries, events or indexes.")}</p>
        </div>
      </div>
      <div className="apf-groundworks-detail-grid">
        {snapshot.groundworks.platformServices.map((service) => {
          const serviceMappingCount = snapshot.groundworks.platformDataMappings.filter((mapping) => mapping.serviceId === service.id).length;
          return (
            <article className="apf-surface apf-groundworks-panel" id={service.id} key={service.id}>
              <div className="apf-panel-header"><div><p className="apf-section-heading__eyebrow">{copy("page.groundworks.platform-services.owner-eyebrow", "Platform service owner")}</p><h3>{service.label}</h3><code>{service.id}</code></div></div>
              <p>{service.mission}</p>
              <dl className="apf-definition-list">
                <div><dt>{copy("page.groundworks.platform-services.contract.boundary", "Boundary")}</dt><dd>{service.ownershipBoundary}</dd></div>
                <div><dt>{copy("page.groundworks.platform-services.contract.interfaces", "Interfaces")}</dt><dd><CodeValues values={service.interfaces} /></dd></div>
                <div><dt>{copy("page.groundworks.platform-services.contract.mapping-count", "Mapping contracts")}</dt><dd>{serviceMappingCount}</dd></div>
              </dl>
              <h4>{copy("page.groundworks.platform-services.owned-title", "Owned entity records")}</h4>
              <ul className="apf-plain-list">
                {service.ownedEntityIds.map((id) => <li key={id}><Link to={groundworksEntityPath(id)}>{entityNames.get(id) ?? humanize(id)}<ArrowRight size={16} aria-hidden="true" /></Link></li>)}
              </ul>
            </article>
          );
        })}
      </div>
    </>
  );
}

function PlatformMappingsSection() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const entityNames = new Map(snapshot.groundworks.entities.map((entity) => [entity.id, entity.label]));
  const platformServiceNames = new Map(snapshot.groundworks.platformServices.map((service) => [service.id, service.label]));

  return (
    <>
      <div className="apf-section-heading">
        <div>
          <h2 id="data-model-section-title">{copy("page.groundworks.platform-mappings.title", "Platform data mapping controls")}</h2>
          <p>{copy("page.groundworks.platform-mappings.description", "Every service operation declares ownership, interface, concurrency, keys, authority, idempotency, reconciliation and last-good behaviour.")}</p>
        </div>
      </div>
      <div className="apf-surface apf-data-table-wrap" role="region" aria-label={copy("page.groundworks.platform-mappings.table-label", "Platform service data mapping contracts")} tabIndex={0}>
        <table className="apf-data-table apf-data-table--responsive apf-pattern-mapping-table">
          <caption className="apf-visually-hidden">{copy("page.groundworks.platform-mappings.table-caption", "Exact platform service data mapping contracts")}</caption>
          <thead><tr><th>{copy("page.groundworks.platform-mappings.column.entity", "Entity")}</th><th>{copy("page.groundworks.platform-mappings.column.platform-owner", "Platform owner")}</th><th>{copy("page.groundworks.platform-mappings.column.operation", "Operation")}</th><th>{copy("page.groundworks.platform-mappings.column.exact-contract", "Exact contract")}</th><th>{copy("page.groundworks.platform-mappings.column.purpose", "Purpose")}</th></tr></thead>
          <tbody>
            {snapshot.groundworks.platformDataMappings.map((mapping) => (
              <tr key={`${mapping.serviceId}-${mapping.operationId}`}>
                <td><Link className="apf-groundworks-entity-link" to={groundworksEntityPath(mapping.entityId)}><strong>{entityNames.get(mapping.entityId) ?? humanize(mapping.entityId)}</strong><code>{mapping.entityId}</code></Link></td>
                <td data-label={copy("page.groundworks.platform-mappings.column.platform-owner", "Platform owner")}><strong>{platformServiceNames.get(mapping.serviceId) ?? humanize(mapping.serviceId)}</strong><small>{mapping.serviceId}</small></td>
                <td className="apf-mapping-operation" data-label={copy("page.groundworks.platform-mappings.column.operation", "Operation")}><span className="apf-identity-meta">{humanize(mapping.access)}</span><code>{mapping.operationId}</code></td>
                <td data-label={copy("page.groundworks.platform-mappings.column.exact-contract", "Exact contract")}><details className="apf-mapping-disclosure"><summary>{copy("page.groundworks.platform-mappings.show-controls", "Show exact mapping controls")}</summary><MappingContract mapping={mapping} /></details></td>
                <td data-label={copy("page.groundworks.platform-mappings.column.purpose", "Purpose")}>{mapping.purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function GroundworksPage() {
  const { entityId } = useParams();
  const { snapshot } = useFramework();
  const copy = useCopy();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectedDimensions = useMemo(() => resolveGroundworksDimensions(snapshot, copy), [copy, snapshot]);
  const requestedDomainId = searchParams.get("domain");
  const urlDomainId = requestedDomainId && snapshot.content.domainWorkblocks.some((workblock) => workblock.id === requestedDomainId)
    ? requestedDomainId
    : "all";
  const requestedDimensionIds = useMemo(() => uniqueStrings(
    searchParams.getAll("dimension").filter((dimensionId) =>
      projectedDimensions.some((dimension) => dimension.id === dimensionId)
    )
  ), [projectedDimensions, searchParams]);
  const [railFilter, setRailFilter] = useState("");
  const [query, setQuery] = useState("");
  const domainId = urlDomainId;
  const [kind, setKind] = useState("all");
  const [surface, setSurface] = useState("all");
  const [standardId, setStandardId] = useState("all");
  const selected = entityId ? snapshot.groundworks.entities.find((entity) => entity.id === entityId) : undefined;
  const dimensionIds = requestedDimensionIds;

  const domains = [...snapshot.content.domainWorkblocks].sort((a, b) => a.order - b.order);

  const groups: IndexGroup[] = useMemo(() => [
    { id: "start", label: copy("page.groundworks.index.group.start", "Start here"), entries: [{ id: "overview", label: copy("page.groundworks.index.overview", "Overview"), keywords: copy("page.groundworks.index.overview.keywords", "counts governance versioning ownership interoperability change") }] },
    { id: "shared", label: copy("page.groundworks.index.group.shared", "Shared contracts"), entries: sharedSections.map((entry) => ({ ...entry, label: copy(entry.labelKey, entry.label), keywords: copy(entry.keywordsKey, entry.keywords) })) },
    {
      id: "systems",
      label: copy("page.groundworks.index.group.systems", "Filter by domain"),
      entries: domains.map((domain) => {
        return {
          id: domain.id,
          label: copy(domain.nameKey),
          keywords: [
            copy(domain.labelKey),
            copy(domain.shortLabelKey),
            copy(domain.descriptorKey),
            copy("page.groundworks.index.domain.keywords", "canonical entities records fields owner ownership")
          ].join(" ")
        };
      })
    }
  ], [copy, domains]);

  const sectionIds = useMemo(() => new Set(["overview", ...sharedSections.map((entry) => entry.id)]), []);
  const hash = decodeURIComponent(location.hash.slice(1));
  const requestedSection = searchParams.get("section") ?? legacyAnchors[hash] ?? (sectionIds.has(hash) ? hash : undefined);
  const activeSection = requestedSection && sectionIds.has(requestedSection)
    ? requestedSection
    : urlDomainId !== "all" || requestedDimensionIds.length
      ? "entities"
      : "overview";

  useEffect(() => {
    if (location.hash !== `#${catalogueContentTargetId}`) return;
    const target = document.getElementById(catalogueContentTargetId);
    if (!target) return;
    if (typeof target.scrollIntoView === "function") target.scrollIntoView();
    target.focus({ preventScroll: true });
  }, [activeSection, location.hash]);

  const sectionLink = (id: string) => {
    const next = new URLSearchParams(searchParams);
    const domainLink = domains.some((domain) => domain.id === id);
    if (domainLink) {
      next.set("domain", id);
      next.set("section", "entities");
    } else {
      next.set("section", id);
    }
    return `/groundworks?${next.toString()}#${catalogueContentTargetId}`;
  };

  const railMatches = (entry: IndexEntry) => {
    const normalizedFilter = railFilter.trim().toLowerCase();
    if (!normalizedFilter) return true;
    return `${entry.label} ${entry.keywords}`.toLowerCase().includes(normalizedFilter);
  };

  const visibleGroups = groups
    .map((group) => ({ ...group, entries: group.entries.filter(railMatches) }))
    .filter((group) => group.entries.length > 0);

  const resetFilters = () => {
    setQuery("");
    setKind("all");
    setSurface("all");
    setStandardId("all");
    const next = new URLSearchParams(searchParams);
    next.delete("domain");
    next.delete("dimension");
    setSearchParams(next, { replace: true });
  };

  const updateDomainFilter = (nextDomainId: string) => {
    const next = new URLSearchParams(searchParams);
    if (nextDomainId === "all") next.delete("domain");
    else next.set("domain", nextDomainId);
    setSearchParams(next, { replace: true });
  };

  const updateDimensionFilter = (nextDimensionIds: string[]) => {
    const nextIds = uniqueStrings(nextDimensionIds);
    const next = new URLSearchParams(searchParams);
    next.delete("dimension");
    nextIds.forEach((dimensionId) => next.append("dimension", dimensionId));
    setSearchParams(next, { replace: true });
  };

  if (entityId && selected) return <EntityDetail entity={selected} />;
  if (entityId && !selected) {
    return <div className="apf-page apf-register-reference apf-data-model"><AbsenceBlock eyebrow={copy("absence.entity.eyebrow", "Data-model reference")} title={copy("absence.entity.title", "Entity not found")} support={copy("absence.entity.support", "This entity is not part of the current data-model reference.")} headingLevel={1} holds={[{ label: copy("absence.holding.entities", "Shared entities"), count: snapshot.groundworks.entities.length }, { label: copy("absence.holding.workblocks", "Domain Workblocks"), count: snapshot.content.domainWorkblocks.length }, { label: copy("absence.holding.business-modules", "Business modules"), count: snapshot.businessModuleRegistry.businessModules.length }]} routes={<Link to="/groundworks">{copy("absence.entity.route", "Return to the data model")}</Link>} /></div>;
  }

  const filters: RegistryFilters = { query, domainId, dimensionIds, kind, surface, standardId };
  const filterBarSections = new Set(["entities", "fields"]);

  return (
    <div className="apf-page apf-register-reference apf-page--master-detail apf-data-model">
      <PageHeader
        eyebrow={copy("page.groundworks.eyebrow")}
        title={copy("page.groundworks.title")}
        description={copy("page.groundworks.description")}
      />

      <PlaceLine
        section={placePart(copy, "place.data-model.section")}
        segments={[{
          text: placePart(copy, "place.data-model.scope")
            .replace("{count}", String(snapshot.groundworks.entities.length))
        }]}
        cut={placePart(copy, "place.data-model.version")
          .replace("{version}", snapshot.frameworkVersion) || undefined}
      />

      <div className="apf-master-detail">
        <div className="apf-master-detail__rail">
          <nav className="apf-index-rail" aria-label={copy("page.groundworks.index.nav-label", "Data model index")}>
            <label className="apf-search-field apf-index-rail__filter">
              <Search aria-hidden="true" />
              <span className="apf-visually-hidden">{copy("page.groundworks.index.filter-label", "Filter the index")}</span>
              <input value={railFilter} onChange={(event) => setRailFilter(event.target.value)} placeholder={copy("page.groundworks.index.filter-label", "Filter the index")} type="search" />
            </label>
            {visibleGroups.map((group) => (
              <div className="apf-index-rail__group" key={group.id}>
                <p className="apf-index-rail__group-label">{group.label}</p>
                <ul className="apf-index-rail__list">
                  {group.entries.map((entry) => (
                    <li key={entry.id}>
                      <Link
                        className={`apf-index-rail__link${entry.id === activeSection || (group.id === "systems" && entry.id === domainId) ? " apf-index-rail__link--active" : ""}`}
                        to={sectionLink(entry.id)}
                        aria-current={entry.id === activeSection ? "page" : undefined}
                      >
                        {entry.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {!visibleGroups.length ? <p className="apf-index-rail__empty">{copy("page.groundworks.index.empty", "No section matches that filter.")}</p> : null}
          </nav>
        </div>

        <section
          className="apf-master-detail__pane"
          id={catalogueContentTargetId}
          tabIndex={-1}
          aria-labelledby="data-model-section-title"
        >
          {filterBarSections.has(activeSection) ? (
            <div className="apf-filter-bar apf-groundworks-filters">
              <label className="apf-search-field">
                <Search aria-hidden="true" />
                <span className="apf-visually-hidden">{copy("page.groundworks.filters.search-label", "Search the data model")}</span>
                <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={copy("page.groundworks.filters.search-placeholder", "Search entities, fields, value schemas, services, operations or standards paths")} type="search" />
              </label>
              <label><span>{copy("page.groundworks.filters.domain", "Domain")}</span><select value={domainId} onChange={(event) => updateDomainFilter(event.target.value)}><option value="all">{copy("page.groundworks.filters.domain-all", "All domains")}</option>{domains.map((domain) => <option key={domain.id} value={domain.id}>{copy(domain.shortLabelKey)}</option>)}</select></label>
              {projectedDimensions.length ? (
                <label>
                  <span>{copy("page.groundworks.filters.dimension", "Dimension")}</span>
                  <select
                    multiple
                    value={dimensionIds}
                    onChange={(event) => updateDimensionFilter(Array.from(event.target.selectedOptions, (option) => option.value))}
                  >
                    {projectedDimensions.map((dimension) => <option key={dimension.id} value={dimension.id}>{dimension.label}</option>)}
                  </select>
                </label>
              ) : null}
              <label><span>{copy("page.groundworks.filters.kind", "Kind")}</span><select value={kind} onChange={(event) => setKind(event.target.value)}><option value="all">{copy("page.groundworks.filters.kind-all", "All kinds")}</option>{[...new Set(snapshot.groundworks.entities.map((entity) => entity.kind))].sort().map((value) => <option key={value} value={value}>{humanize(value)}</option>)}</select></label>
              <label><span>{copy("page.groundworks.filters.storage-class", "Storage class")}</span><select value={surface} onChange={(event) => setSurface(event.target.value)}><option value="all">{copy("page.groundworks.filters.storage-class-all", "All storage classes")}</option>{[...new Set(snapshot.groundworks.entities.map((entity) => entity.storageClass))].sort().map((value) => <option key={value} value={value}>{humanize(value)}</option>)}</select></label>
              <label><span>{copy("page.groundworks.filters.standard", "Standard")}</span><select value={standardId} onChange={(event) => setStandardId(event.target.value)}><option value="all">{copy("page.groundworks.filters.standard-all", "All standards")}</option>{snapshot.groundworks.standards.map((standard) => <option key={standard.id} value={standard.id}>{standard.label}</option>)}</select></label>
              <button className="apf-button apf-button--quiet" type="button" onClick={resetFilters}>{copy("page.groundworks.filters.clear", "Clear")}</button>
            </div>
          ) : null}

          {activeSection === "overview" ? <OverviewSection /> : null}
          {activeSection === "entities" ? <EntitiesSection filters={filters} /> : null}
          {activeSection === "fields" ? <FieldsSection filters={filters} /> : null}
          {activeSection === "dimensions" ? <DimensionsSection /> : null}
          {activeSection === "record-envelope" ? <RecordEnvelopeSection /> : null}
          {activeSection === "structured-value-schemas" ? <ValueSchemasSection /> : null}
          {activeSection === "standards-registry" ? <StandardsSection filters={filters} /> : null}
          {activeSection === "registry-vocabulary" ? <VocabularySection /> : null}
          {activeSection === "platform-services" ? <PlatformServicesSection /> : null}
          {activeSection === "platform-mappings" ? <PlatformMappingsSection /> : null}
        </section>
      </div>
    </div>
  );
}
